import React, { useState, useRef } from 'react';
import { signIn, complete2FALogin, API_BASE_URL } from '../services/api';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import PasswordInput from '../components/PasswordInput';

const T = {
  teal: '#5EEAD4', tealDark: '#14B8A6',
  lavender: '#C4B5FD', lavenderDark: '#8B5CF6',
  coral: '#FDA4AF',
};

const GlassCard: React.FC<{ children: React.ReactNode; className?: string }> = ({
  children, className = '',
}) => (
  <div
    className={`rounded-3xl border shadow-xl ${className}`}
    style={{
      background: 'rgba(255,255,255,0.75)',
      backdropFilter: 'blur(24px) saturate(1.2)',
      WebkitBackdropFilter: 'blur(24px) saturate(1.2)',
      borderColor: 'rgba(255,255,255,0.6)',
      boxShadow: '0 8px 32px rgba(148,163,184,0.12)',
    }}
  >
    {children}
  </div>
);

const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    userType: 'rider' as 'driver' | 'rider',
  });
  const [isLoading, setIsLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  
  // 2FA state
  const [twoFactorStep, setTwoFactorStep] = useState(false);
  const [pendingToken, setPendingToken] = useState('');
  const [totpCode, setTotpCode] = useState(['', '', '', '', '', '']);
  const totpInputRefs = useRef<(HTMLInputElement | null)[]>([]);

  const redirectToApp = (accessToken: string) => {
    const appUrl = process.env.NODE_ENV === 'development'
      ? 'http://localhost:3002'
      : 'https://app.karvlo.com';
    window.location.href = `${appUrl}?token=${accessToken}`;
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError('');
    try {
      const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '778993316075-ugg567rrvjruor2an2liuaunkg8cquf0.apps.googleusercontent.com';
      if (!googleClientId) {
        setError('Google Sign-In is not configured.');
        setGoogleLoading(false);
        return;
      }
      const google = (window as any).google;
      if (!google?.accounts?.id) {
        setError('Google Sign-In not available. Please check your connection.');
        setGoogleLoading(false);
        return;
      }
      google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async (response: any) => {
          try {
            const res = await fetch(`${API_BASE_URL}/api/auth/google`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                idToken: response.credential,
                userType: formData.userType,
              }),
            });
            const data = await res.json();
            if (res.ok) {
              localStorage.setItem('accessToken', data.accessToken);
              if (data.refreshToken) localStorage.setItem('refreshToken', data.refreshToken);
              localStorage.setItem('userType', data.user?.userType || formData.userType);
              localStorage.setItem('user', JSON.stringify(data.user));
              setSuccess(true);
              setTimeout(() => redirectToApp(data.accessToken), 1500);
            } else {
              setError(data.error || 'Google sign-in failed');
            }
          } catch {
            setError('Network error during Google sign-in');
          } finally {
            setGoogleLoading(false);
          }
        },
      });
      google.accounts.id.prompt();
    } catch {
      setError('Failed to initialize Google Sign-In');
      setGoogleLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');
    try {
      const response = await signIn(formData.email, formData.password, formData.userType);
      
      // 2FA required — show TOTP input step
      if (response.success && response.requiresTwoFactor && response.pendingToken) {
        setPendingToken(response.pendingToken);
        setTwoFactorStep(true);
        setIsLoading(false);
        return;
      }
      
      if (response.success && response.data) {
        setSuccess(true);
        localStorage.setItem('accessToken', response.data.tokens.accessToken);
        localStorage.setItem('refreshToken', response.data.tokens.refreshToken);
        localStorage.setItem('userType', response.data.user.userType);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setTimeout(() => {
          if (response.data && response.data.tokens) {
            redirectToApp(response.data.tokens.accessToken);
          }
        }, 2000);
      } else {
        setError(response.message || 'Sign in failed');
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred during sign in');
    } finally {
      setIsLoading(false);
    }
  };

  const handleTotpChange = (index: number, value: string) => {
    if (value.length > 1) value = value.slice(-1);
    if (value && !/^\d$/.test(value)) return;
    const newCode = [...totpCode];
    newCode[index] = value;
    setTotpCode(newCode);
    // Auto-advance to next input
    if (value && index < 5) {
      totpInputRefs.current[index + 1]?.focus();
    }
    // Auto-submit when all 6 digits entered
    if (value && index === 5 && newCode.every(d => d !== '')) {
      handle2FASubmit(newCode.join(''));
    }
  };

  const handleTotpKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !totpCode[index] && index > 0) {
      totpInputRefs.current[index - 1]?.focus();
    }
  };

  const handle2FASubmit = async (codeOverride?: string) => {
    const code = codeOverride || totpCode.join('');
    if (code.length !== 6) {
      setError('Please enter the full 6-digit code');
      return;
    }
    setIsLoading(true);
    setError('');
    try {
      const response = await complete2FALogin(pendingToken, code);
      if (response.success && response.data) {
        setSuccess(true);
        localStorage.setItem('accessToken', response.data.tokens.accessToken);
        localStorage.setItem('refreshToken', response.data.tokens.refreshToken);
        localStorage.setItem('userType', response.data.user.userType);
        localStorage.setItem('user', JSON.stringify(response.data.user));
        setTimeout(() => {
          if (response.data && response.data.tokens) {
            redirectToApp(response.data.tokens.accessToken);
          }
        }, 2000);
      } else {
        setError(response.message || '2FA verification failed');
      }
    } catch (err: any) {
      setError(err.message || '2FA verification failed');
      setTotpCode(['', '', '', '', '', '']);
      totpInputRefs.current[0]?.focus();
    } finally {
      setIsLoading(false);
    }
  };

  const handleSignUpRedirect = () => {
    navigate('/', { replace: true });
    setTimeout(() => {
      const signupSection = document.getElementById('scene-signup');
      if (signupSection) {
        signupSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 500);
  };

  const inputClass =
    'w-full px-4 py-3 rounded-xl bg-white/50 border border-slate-200/60 text-slate-800 placeholder-slate-400 focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-200/50 transition-all';

  return (
    <div className="relative z-10 min-h-screen flex items-center justify-center px-4 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md"
      >
        <GlassCard className="p-8 sm:p-10">
          {/* Header */}
          <div className="text-center mb-8">
            <h1 className="text-3xl font-black text-slate-800 mb-1">Welcome back</h1>
            <p className="text-slate-500">Sign in to continue to Karvlo</p>
          </div>

          {/* Success */}
          {success && (
            <div
              className="mb-6 px-4 py-3 rounded-xl text-sm border"
              style={{
                background: 'rgba(240,253,250,0.8)',
                borderColor: 'rgba(153,246,228,0.5)',
                color: T.tealDark,
              }}
            >
              ✅ Sign in successful! Redirecting...
            </div>
          )}

          {/* Error */}
          {error && (
            <div
              className="mb-6 px-4 py-3 rounded-xl text-sm border text-rose-600"
              style={{
                background: 'rgba(255,241,242,0.8)',
                borderColor: 'rgba(254,205,211,0.5)',
              }}
            >
              {error}
            </div>
          )}

          {/* Form */}
          {twoFactorStep ? (
            /* 2FA Code Entry */
            <div className="space-y-5">
              <div className="text-center mb-2">
                <div className="text-3xl mb-2">🔐</div>
                <h2 className="text-lg font-bold text-slate-800">Two-Factor Authentication</h2>
                <p className="text-sm text-slate-500 mt-1">Enter the 6-digit code from your authenticator app</p>
              </div>

              <div className="flex justify-center gap-2">
                {totpCode.map((digit, i) => (
                  <input
                    key={i}
                    ref={el => { totpInputRefs.current[i] = el; }}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={e => handleTotpChange(i, e.target.value)}
                    onKeyDown={e => handleTotpKeyDown(i, e)}
                    className="w-12 h-14 text-center text-2xl font-bold rounded-xl bg-white/50 border border-slate-200/60 text-slate-800 focus:border-teal-400 focus:outline-none focus:ring-2 focus:ring-teal-200/50 transition-all"
                    autoFocus={i === 0}
                  />
                ))}
              </div>

              <button
                type="button"
                onClick={() => handle2FASubmit()}
                disabled={isLoading || totpCode.join('').length !== 6}
                className="w-full py-3.5 bg-slate-800 text-white font-bold rounded-2xl shadow-lg shadow-slate-300/40 hover:bg-slate-700 hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Verifying...' : 'Verify Code'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setTwoFactorStep(false);
                  setPendingToken('');
                  setTotpCode(['', '', '', '', '', '']);
                  setError('');
                }}
                className="w-full text-sm text-slate-400 hover:text-slate-600 transition-colors"
              >
                ← Back to login
              </button>
            </div>
          ) : (
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* User Type Toggle */}
            <div className="flex gap-2 p-1 rounded-xl" style={{ background: 'rgba(241,245,249,0.6)' }}>
              {(['rider', 'driver'] as const).map(type => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, userType: type }))}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-semibold transition-all duration-300 capitalize ${
                    formData.userType === type
                      ? 'bg-white text-slate-800 shadow-sm'
                      : 'text-slate-400 hover:text-slate-600'
                  }`}
                >
                  {type === 'rider' ? '🚶 Rider' : '🚗 Driver'}
                </button>
              ))}
            </div>

            {/* Email */}
            <div>
              <label className="block text-sm font-medium text-slate-600 mb-1.5">Email</label>
              <input
                type="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={inputClass}
                placeholder="you@example.com"
                required
              />
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <label className="text-sm font-medium text-slate-600">Password</label>
                <button
                  type="button"
                  onClick={() => navigate('/forgot-password')}
                  className="text-xs font-medium transition-colors"
                  style={{ color: T.tealDark }}
                >
                  Forgot?
                </button>
              </div>
              <PasswordInput
                name="password"
                value={formData.password}
                onChange={(value) => setFormData(prev => ({ ...prev, password: value }))}
                className="bg-white/50 border border-slate-200/60 rounded-xl focus:ring-0 focus:border-teal-400"
                placeholder="Enter your password"
                required
              />
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={isLoading}
              className="w-full py-3.5 bg-slate-800 text-white font-bold rounded-2xl shadow-lg shadow-slate-300/40 hover:bg-slate-700 hover:shadow-xl transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>
          )}

          {/* Divider */}
          {!twoFactorStep && (
          <>
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200/60" />
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="bg-white/60 px-3 text-slate-400">or</span>
            </div>
          </div>

          {/* Google */}
          <button
            onClick={handleGoogleSignIn}
            disabled={isLoading || googleLoading}
            className="w-full flex items-center justify-center gap-3 px-4 py-3 rounded-xl bg-white/50 border border-slate-200/60 hover:bg-white/80 hover:shadow-md transition-all duration-300 disabled:opacity-50"
          >
            {googleLoading ? (
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-slate-400" />
            ) : (
              <>
                <svg className="w-5 h-5" viewBox="0 0 24 24">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
                <span className="text-sm font-semibold text-slate-600">Continue with Google</span>
              </>
            )}
          </button>

          {/* Footer */}
          <div className="mt-8 text-center space-y-3">
            <button
              onClick={() => navigate('/')}
              className="text-sm text-slate-400 hover:text-slate-600 transition-colors"
            >
              ← Back to Home
            </button>
            <div className="pt-3 border-t border-slate-100/60">
              <p className="text-sm text-slate-400 mb-2">Don't have an account?</p>
              <button
                onClick={handleSignUpRedirect}
                className="w-full py-3 rounded-xl font-semibold text-sm border border-slate-200/60 text-slate-600 hover:bg-slate-50/50 hover:shadow-sm transition-all"
              >
                Join Beta Waitlist
              </button>
            </div>
          </div>
          </>
          )}
        </GlassCard>
      </motion.div>
    </div>
  );
};

export default LoginPage;
